import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLogger } from "../../../../contexts/LoggerContext";

// Reuse these functions from GainLossSlider
const calculateValidRange = (
  ballOn: number | null
): { min: number; max: number } => {
  if (ballOn === null) {
    return { min: -99, max: 99 };
  }
  const minValue = ballOn > 0 ? -(50 - ballOn + 50) : ballOn + 1;
  const maxValue = ballOn > 0 ? ballOn - 1 : 50 - Math.abs(ballOn) + 49;

  return { min: minValue, max: maxValue };
};

const calculateNewSpot = (ballOn: number | null, gainLoss: number): number => {
  if (ballOn === null) return 0;

  const startInOwnTerritory = ballOn < 0;
  let newSpot: number = 0;

  if (startInOwnTerritory) {
    newSpot = ballOn + -gainLoss;
    if (newSpot <= -50) {
      newSpot = 50 - (Math.abs(newSpot) - 50);
    }
  } else {
    newSpot = ballOn - gainLoss;
    if (newSpot >= 50) {
      newSpot = -(50 - (newSpot - 50));
    }
  }
  return newSpot;
};

export const GainLossInput: React.FC<{
  value: number;
  onChange: (value: number) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const { gameSituation, touchdown, safety, setTouchdown, setSafety, setGameSituation } = useLogger();
  const { min, max } = calculateValidRange(gameSituation.yardline);
  const newSpot = calculateNewSpot(gameSituation.yardline, value);

  // Keep track of what's in the input field
  const [inputValue, setInputValue] = React.useState<string>(value.toString());

  // Update input value when prop value changes
  React.useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);
    
    // Handle empty input or just a minus sign - don't update the actual value yet
    if (newInputValue === '' || newInputValue === '-') {
      return;
    }

    // Parse the input value
    const newValue = parseInt(newInputValue, 10);
    if (!isNaN(newValue)) {
      if (newValue >= min && newValue <= max) {
        onChange(newValue);
      }
    }
  };

  // Handle blur to reset to valid value if needed
  const handleBlur = () => {
    setInputValue(value.toString());
  };

  const handleTouchdown = () => {
    setTouchdown(!touchdown);
    onChange(0);
    if (!touchdown && gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        club_score: gameSituation.club_score + 6,
      });
    } else if (touchdown && gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        club_score: gameSituation.club_score - 6,
      });
    } else if (!touchdown && !gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score + 6,
      });
    } else if (touchdown && !gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score - 6,
      });
    }
  };

  const handleSafety = () => {
    setSafety(!safety);
    onChange(0);
    if (!safety && gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score + 2,
      });
    } else if (safety && gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score - 2,
      });
    } else if (!safety && !gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        club_score: gameSituation.club_score + 2,
      });
    } else if (safety && !gameSituation.club_possession) {
      setGameSituation({
        ...gameSituation,
        club_score: gameSituation.club_score - 2,
      });
    }
  };

  return (
    <Box sx={{ width: "100%", px: 2 }}>
      {/* Details Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          mb: 4,
        }}
      >
        {/* Gain/Loss Label */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mr: 2,
          }}
        >
          <Typography id={`${label}-input-label`} gutterBottom>
            Gain/Loss
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => {
                const newValue = value - 1;
                if (newValue >= min) {
                  onChange(newValue);
                }
              }}
              sx={{ 
                p: 1.5,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'white'
                }
              }}
            >
              <KeyboardArrowDownIcon sx={{ fontSize: '2rem' }} />
            </IconButton>
            <TextField
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              inputProps={{
                min,
                max,
                style: { 
                  textAlign: "center",
                },
              }}
              sx={{ 
                width: 80,
                '& input': {
                  p: 1,
                  fontSize: '1.1rem',
                  fontWeight: 'medium',
                  // Hide spinner buttons
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '&[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }
              }}
            />
            <IconButton 
              onClick={() => {
                const newValue = value + 1;
                if (newValue <= max) {
                  onChange(newValue);
                }
              }}
              sx={{ 
                p: 1.5,
                color: 'success.main',
                '&:hover': {
                  backgroundColor: 'success.light',
                  color: 'white'
                }
              }}
            >
              <KeyboardArrowUpIcon sx={{ fontSize: '2rem' }} />
            </IconButton>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{
              mt: 0.5,
              color:
                value > 0
                  ? "success.main"
                  : value < 0
                  ? "error.main"
                  : "text.primary",
            }}
          >
            {value > 0 ? `+${value}` : value} Yards
          </Typography>
        </Box>

        {/* Ball Position */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            ml: 2,
          }}
        >
          <Typography id={`${label}-position-label`} gutterBottom>
            Ball Spotted
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color:
                value > 0
                  ? "success.main"
                  : value < 0
                  ? "error.main"
                  : "text.primary",
            }}
          >
            {newSpot} Yard Line
          </Typography>
        </Box>
      </Box>

      {/* Touchdown and Safety Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Button
          variant={safety ? "contained" : "outlined"}
          color={safety ? "error" : "primary"}
          sx={{ minWidth: 150, fontWeight: safety ? "bold" : "normal" }}
          onClick={handleSafety}
        >
          Safety
        </Button>
        <Button
          variant={touchdown ? "contained" : "outlined"}
          color={touchdown ? "success" : "primary"}
          sx={{ minWidth: 150, fontWeight: touchdown ? "bold" : "normal" }}
          onClick={handleTouchdown}
        >
          Touchdown
        </Button>
      </Box>
    </Box>
  );
};